import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you — our team will contact you shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 sm:py-32 px-6 sm:px-10 lg:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl">
          <div className="section-eyebrow" data-testid="contact-eyebrow">Contact Us</div>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900" data-testid="contact-heading">
            Let's build a healthier <span className="text-gradient">India, together</span>.
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Whether you're a healthcare professional, distributor, or curious partner — reach out.
            Our team responds within one business day.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-5 gap-8 lg:gap-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 space-y-4"
          >
            <div className="bg-gradient-to-br from-blue-900 to-blue-950 text-white rounded-3xl p-8 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-white/10 flex items-center justify-center">
                  <MapPin size={18} />
                </div>
                <div>
                  <div className="text-[0.65rem] tracking-[0.24em] uppercase text-blue-200">Address</div>
                  <div className="font-display text-lg font-semibold">Andrich Pharmaceuticals Pvt. Ltd.</div>
                </div>
              </div>
              <div className="mt-6 space-y-4">
                <a href="mailto:andrichpharmacueticalpvtltd@gmail.com" className="flex items-center gap-3 group" data-testid="contact-email-link">
                  <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-green-500 transition-colors">
                    <Mail size={16} />
                  </div>
                  <div>
                    <div className="text-[0.65rem] tracking-[0.24em] uppercase text-blue-200">Email</div>
                    <div className="text-sm font-medium break-all">andrichpharmacueticalpvtltd@gmail.com</div>
                  </div>
                </a>
                <a href="tel:+919598643434" className="flex items-center gap-3 group" data-testid="contact-phone-link">
                  <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-green-500 transition-colors">
                    <Phone size={16} />
                  </div>
                  <div>
                    <div className="text-[0.65rem] tracking-[0.24em] uppercase text-blue-200">Phone</div>
                    <div className="text-sm font-medium">+91 9598643434</div>
                  </div>
                </a>
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden border border-slate-200 h-64 relative bg-slate-100" data-testid="contact-map">
              <iframe
                title="Andrich Location"
                src="https://www.google.com/maps?q=Lucknow%2C+Uttar+Pradesh%2C+India&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            onSubmit={onSubmit}
            className="lg:col-span-3 bg-white border border-slate-200 rounded-3xl p-8 shadow-sm"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full Name" name="name" value={form.name} onChange={onChange} required testId="contact-name" />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testId="contact-email" />
              <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testId="contact-phone" />
              <Field label="Subject" name="subject" value={form.subject} onChange={onChange} testId="contact-subject" />
            </div>
            <div className="mt-4">
              <label className="text-[0.7rem] tracking-[0.22em] uppercase text-slate-500 font-medium">Message</label>
              <textarea
                name="message"
                rows={5}
                value={form.message}
                onChange={onChange}
                required
                className="mt-2 w-full px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm resize-none"
                placeholder="Tell us about your enquiry…"
                data-testid="contact-message"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary mt-6 w-full sm:w-auto disabled:opacity-70"
              data-testid="contact-submit"
            >
              {submitting ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Sending…</>) : (<>Send Message <Send size={14} className="ml-2" /></>)}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", required, testId }) {
  return (
    <label className="block">
      <span className="text-[0.7rem] tracking-[0.22em] uppercase text-slate-500 font-medium">{label}{required && "*"}</span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="mt-2 w-full px-4 py-3 rounded-full bg-slate-50 border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
        data-testid={testId}
      />
    </label>
  );
}
